import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subject, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { takeUntil } from 'rxjs/operators';
import { SSOService, SSOUser } from '../../services/sso.service';
import { TopMenuComponent } from '../../shared/components/top-menu/top-menu';
import { ADMIN_ROLE_SET, ROLE_NAMES } from '../../constants/role.constants';
import { SidebarStateService } from '../../services/sidebar-state.service';
import { AdminService } from '../../services/admin.service';
import { AppFeatureStateService } from '../../services/app-feature-state.service';
import { APP_MENU_FEATURE_KEYS, AppSidebarMenuItem } from '../../constants/app-menu.constants';
import { ChatBubbleComponent } from '../../shared/components/chat-bubble/chat-bubble';

@Component({
  selector: 'app-main-layout',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, TopMenuComponent, CommonModule, ChatBubbleComponent],
  templateUrl: './main-layout.html',
  styleUrl: './main-layout.css',
})
export class MainLayout implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  isSuperAdmin = signal(false);
  sidebarSectionLabel = signal('MENU');
  sidebarMenuItems = signal<AppSidebarMenuItem[]>([]);

  get sidebarCollapsed(): boolean {
    return this.sidebarState.collapsed();
  }

  toggleSidebar(): void {
    this.sidebarState.toggle();
  }

  private readonly allMenuItems: AppSidebarMenuItem[] = [
    { label: 'Home', icon: 'ri-home-4-line', routerLink: ['/home'], featureKey: APP_MENU_FEATURE_KEYS.HOME },
    { label: 'Projects', icon: 'ri-briefcase-line', routerLink: ['/projects'], featureKey: APP_MENU_FEATURE_KEYS.PROJECTS },
    { label: 'Files Repository', icon: 'ri-folder-line', routerLink: ['/app-files'], featureKey: APP_MENU_FEATURE_KEYS.FILES },
    { label: 'User Management', icon: 'ri-group-line', routerLink: ['/users'], featureKey: APP_MENU_FEATURE_KEYS.USER_MANAGEMENT },
    { label: 'Role Management', icon: 'ri-settings-3-line', routerLink: ['/role-management'], featureKey: APP_MENU_FEATURE_KEYS.ROLE_MANAGEMENT },
    { label: 'Templates', icon: 'ri-stack-line', routerLink: ['/templates'], featureKey: APP_MENU_FEATURE_KEYS.TEMPLATES },
    { label: 'Task Management', icon: 'ri-task-line', routerLink: ['/task-management'], featureKey: APP_MENU_FEATURE_KEYS.TASK_MANAGEMENT },
    { label: 'File Settings', icon: 'ri-folder-settings-line', routerLink: ['/file-settings'], featureKey: APP_MENU_FEATURE_KEYS.FILE_SETTINGS },
    { label: 'Privacy Policy', icon: 'ri-shield-check-line', routerLink: ['/privacy-policy-admin'], featureKey: APP_MENU_FEATURE_KEYS.PRIVACY_POLICY },
    { label: 'App Versions', icon: 'ri-smartphone-line', routerLink: ['/app-versions'], featureKey: APP_MENU_FEATURE_KEYS.APP_VERSIONS },
    { label: 'Integrations', icon: 'ri-links-line', routerLink: ['/integrations'], featureKey: APP_MENU_FEATURE_KEYS.INTEGRATIONS },
    { label: 'Block Schedule', icon: 'ri-calendar-schedule-line', routerLink: ['/block-schedule'], featureKey: APP_MENU_FEATURE_KEYS.BLOCK_SCHEDULE },
    { label: 'Maps', icon: 'ri-map-pin-line', routerLink: ['/maps'], featureKey: APP_MENU_FEATURE_KEYS.MAPS }
  ];

  constructor(
    private ssoService: SSOService,
    private router: Router,
    private sidebarState: SidebarStateService,
    private adminService: AdminService,
    private appFeatureState: AppFeatureStateService
  ) { }

  ngOnInit() {
    this.ssoService.user$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((user: SSOUser | null) => {
      this.updateRole(user);
      this.loadSidebarPermissions(user);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateRole(user: SSOUser | null) {
    const roleName = (user?.roleName || '').trim().toLowerCase();
    const superAdmin = roleName === ROLE_NAMES.SUPER_ADMIN;
    const isAdmin = ADMIN_ROLE_SET.has(roleName);
    this.isSuperAdmin.set(superAdmin);
    this.sidebarSectionLabel.set(isAdmin ? 'ADMIN' : 'MENU');
  }

  private loadSidebarPermissions(user: SSOUser | null): void {
    if (!user?.userId) {
      this.appFeatureState.reset();
      this.sidebarMenuItems.set([]);
      return;
    }

    this.adminService.getUserAdminFeaturePermissions().pipe(
      take(1),
      catchError(() => of([] as string[]))
    ).subscribe((featureKeys) => {
      this.appFeatureState.setPermissions(featureKeys);
      this.sidebarMenuItems.set(
        this.allMenuItems.filter(item => this.appFeatureState.canView(item.featureKey))
      );
    });
  }

  async onLogout() {
    try {
      await this.ssoService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
      localStorage.clear();
      this.router.navigate(['/login']);
    }
  }
}
